import React, { useEffect, useRef, useState } from "react";
import { CheckIcon, CrossIcon } from "../../../assets/icons";

const EMPTY_VALUE = "N/A";

interface IDistrictSchoolInlineEditCellProps {
  schoolId: number;
  value: string;
  isEditing: boolean;
  onStartEdit: (schoolId: number) => void;
  onSave: (schoolId: number, value: string) => void;
  onCancel: () => void;
}

const DistrictSchoolInlineEditCell = (
  props: IDistrictSchoolInlineEditCellProps
) => {
  const { schoolId, value, isEditing, onStartEdit, onSave, onCancel } = props;

  const [editValue, setEditValue] = useState<string>("");
  const editorRef = useRef<HTMLDivElement | null>(null);

  // Any click landing outside the open editor abandons the edit.
  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const handleOutsideMouseDown = (event: MouseEvent) => {
      if (
        editorRef.current &&
        !editorRef.current.contains(event.target as Node)
      ) {
        onCancel();
      }
    };

    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () =>
      document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [isEditing, onCancel]);

  const startEdit = () => {
    setEditValue(value);
    onStartEdit(schoolId);
  };

  return (
    <td className="px-3 py-2 text-sm text-left font-normal">
      {isEditing ? (
        <div ref={editorRef} className="flex w-full items-center gap-2">
          <input
            type="text"
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Escape") {
                onCancel();
              }
            }}
            className="min-w-0 flex-1 rounded border-gray-500 bg-white px-2 py-1 text-sm font-normal focus:border-primary-color focus:ring-1 focus:ring-primary-color"
          />
          <button
            type="button"
            title="Save"
            aria-label="Save"
            className="shrink-0 rounded border border-green-700 p-0.5 text-green-700 hover:bg-green-50"
            onClick={() => onSave(schoolId, editValue)}
          >
            <CheckIcon className="w-4 h-4"></CheckIcon>
          </button>
          <button
            type="button"
            title="Cancel"
            aria-label="Cancel"
            className="shrink-0 rounded border border-red-700 p-0.5 text-red-700 hover:bg-red-50"
            onClick={onCancel}
          >
            <CrossIcon className="w-4 h-4"></CrossIcon>
          </button>
        </div>
      ) : (
        <button
          type="button"
          title="Click to edit"
          className="text-left hover:underline"
          onClick={startEdit}
        >
          {value || EMPTY_VALUE}
        </button>
      )}
    </td>
  );
};

export { DistrictSchoolInlineEditCell };
